import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { AccountProfile, TenantResult } from "@hydrologiq/gql/graphql"
import { PromotionOverrideConfig } from "../utils/promotionResolver"

export interface PreferencesState {
  tenant: string | null
  feature: string | null
  profile: AccountProfile | null
  tenantInformation: TenantResult | null
  promotionOverrides: Record<string, PromotionOverrideConfig> | null
}

const initialState: PreferencesState = {
  tenant: null,
  feature: null,
  profile: null,
  tenantInformation: null,
  promotionOverrides: null,
}

/** Shape of the payload received from the backend getPreferences query */
interface SetPreferencesPayload {
  tenant: string | null
  feature: string | null
  profile: AccountProfile | null
  tenantInformation: TenantResult | null
  promotion_overrides?: Array<{
    sectionKey: string
    prototypeId: string
    bundleBaseUrl: string
    enabled: boolean
  }> | null
}

const preferencesSlice = createSlice({
  name: "preferences",
  initialState,
  reducers: {
    setPreferences: (state, action: PayloadAction<SetPreferencesPayload>) => {
      state.tenant = action.payload.tenant
      state.feature = action.payload.feature
      state.profile = action.payload.profile
      state.tenantInformation = action.payload.tenantInformation

      // Transform promotion_overrides array from backend into a Record keyed by sectionKey
      const overridesArray = action.payload.promotion_overrides
      if (overridesArray && overridesArray.length > 0) {
        const overridesRecord: Record<string, PromotionOverrideConfig> = {}
        for (const override of overridesArray) {
          overridesRecord[override.sectionKey] = {
            sectionKey: override.sectionKey,
            prototypeId: override.prototypeId,
            bundleBaseUrl: override.bundleBaseUrl,
            enabled: override.enabled,
          }
        }
        state.promotionOverrides = overridesRecord
      } else {
        state.promotionOverrides = null
      }
    },
    setTenant: (state, action: PayloadAction<string>) => {
      state.tenant = action.payload
    },
    setProfile: (state, action: PayloadAction<AccountProfile>) => {
      state.profile = action.payload
    },
    setPromotionOverrides: (
      state,
      action: PayloadAction<
        Array<{
          sectionKey: string
          prototypeId: string
          bundleBaseUrl: string
          enabled: boolean
        }> | null | undefined
      >,
    ) => {
      const overridesArray = action.payload
      if (overridesArray && overridesArray.length > 0) {
        const overridesRecord: Record<string, PromotionOverrideConfig> = {}
        for (const override of overridesArray) {
          overridesRecord[override.sectionKey] = {
            sectionKey: override.sectionKey,
            prototypeId: override.prototypeId,
            bundleBaseUrl: override.bundleBaseUrl,
            enabled: override.enabled,
          }
        }
        state.promotionOverrides = overridesRecord
      } else {
        state.promotionOverrides = null
      }
    },
    clearPreferences: (state) => {
      state.tenant = null
      state.feature = null
      state.profile = null
      state.tenantInformation = null
      state.promotionOverrides = null
    },
  },
})

export const { setPreferences, setTenant, setProfile, clearPreferences, setPromotionOverrides } =
  preferencesSlice.actions
export default preferencesSlice.reducer
